import { Component, OnInit, Input } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { LeadService } from 'src/app/domains/sales/lead/services/lead.service';
import { Lead } from 'src/app/domains/sales/lead/models/lead.model';
import { SOURCES } from 'src/app/domains/sales/lead/models/lead.model';
import { MEDIUMS } from 'src/app/domains/sales/lead/models/lead.model';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { Subscription } from 'rxjs';

@Component({
  selector: 'gdp-lead',
  templateUrl: './lead.component.html',
  styleUrls: ['./lead.component.scss']
})
export class LeadComponent implements OnInit {
  public newLeadForm: UntypedFormGroup;
  public selectedValueOrigin: boolean = false;
  public selectedValueSource: boolean = false;
  public loading: boolean = false;
  public language: string;
  public countryList = environment.servedCountries;
  private languageSubscription: Subscription;
  private emailPattern = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
  public sources: string[] = SOURCES;
  public mediums: string[] = MEDIUMS;
  constructor(
    private leadService: LeadService,
    private notification: NotificationService,
    private translateService: TranslateService
  ) { }

  ngOnInit() {
    this.language = this.translateService.currentLang;
    this.languageSubscription = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.language = event.lang;
    });
    this.newLeadForm = new UntypedFormGroup({
      firstName: new UntypedFormControl(null, [Validators.required]),
      lastName: new UntypedFormControl(null),
      company: new UntypedFormControl(null),
      phoneNumber: new UntypedFormControl(null),
      email: new UntypedFormControl(null, [Validators.required, Validators.pattern(this.emailPattern)]),
      seats: new UntypedFormControl(null),
      referralCode: new UntypedFormControl(null),
      country: new UntypedFormControl(null),

      medium: new UntypedFormControl('', [Validators.required]),
      customMedium: new UntypedFormControl(''),

      source: new UntypedFormControl('', [Validators.required]),
      customSource: new UntypedFormControl(''),
      info: new UntypedFormControl(null)
    }, [this.formValidator()]);

  }

  ngOnDestroy() {
    if (this.languageSubscription) this.languageSubscription.unsubscribe();
  }

  isFieldValid(name: string) {
    return (
      this.newLeadForm.get(name).invalid && this.newLeadForm.get(name).dirty
    );
  }

  formValidator() {
    return (g: UntypedFormGroup) => {
      let err = { ...g.errors };
      if (g.value.source === 'other' && g.value.customSource === '' && g.controls.customSource.dirty) {
        err = { ...err, source: 'source is required' }
      } else {
        err.source && delete err.source;
      }

      if (g.value.medium === 'other' && g.value.customMedium === '' && g.controls.customMedium.dirty) {
        err = { ...err, medium: 'medium is required' }
      } else {
        err.medium && delete err.medium;
      }
      return err;
    };
  }

  onSubmit() {
    if (this.newLeadForm.valid) {
      const val = this.newLeadForm.value;
      const payload: Lead = {
        firstName: val.firstName,
        lastName: val.lastName,
        company: val.company || '',
        phoneNumber: val.phoneNumber,
        email: val.email,
        seats: val.seats || 0,
        referralCode: val.referralCode || '',
        medium: val.medium === 'other' ? val.customMedium : val.medium,
        source: val.source === 'other' ? val.customSource : val.source,
        info: val.info || '',
        country: val.country && val.country.iso2
      };
      this.loading = true;
      this.leadService.submitLead(payload)
        .subscribe(
          response => {
            this.loading = false;
            this.notification.success('sales.lead.success');
          }, () => {
            this.loading = false;
          }
        );
    }
  }

  onChangeSource(source: string): void {
    this.newLeadForm.patchValue({ source });
    this.newLeadForm.get('source').markAsDirty();
  }

  get sourceToken() {
    return this.newLeadForm.value.source === '' ? 'common.select' : `sales.lead.form.source.options.${this.newLeadForm.value.source}`;
  }

  onChangeMedium(medium: string): void {
    this.newLeadForm.patchValue({ medium });
    this.newLeadForm.get('medium').markAsDirty();
  }

  get mediumToken() {
    return this.newLeadForm.value.medium === '' ? 'common.select' : `sales.lead.form.medium.options.${this.newLeadForm.value.medium}`;
  }

}
